/**
 * Cómo se cuenta una sincronización del SAT en pantalla.
 *
 * Vive aparte porque las mismas cifras aparecen en tres sitios —la fila del
 * histórico, la pantalla del SAT y el detalle de una solicitud— y tenían copias
 * distintas de estas funciones. Una misma sincronización debe leerse igual en
 * los tres; con el texto duplicado era cuestión de tiempo que divergieran.
 */

/** "05/2026" → "01/05/2026". Se parte la cadena para no cruzar husos horarios. */
export const fechaCorta = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

export const periodoDe = (s) => `${fechaCorta(s?.date_from)} → ${fechaCorta(s?.date_to)}`;

export const fechaHora = (iso, { mesLargo = false } = {}) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit',
    month: mesLargo ? 'long' : '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Hora en 24 h: es un registro de proceso y se lee en columna. Con el formato
 * por defecto de es-MX salía "01:35:03 p. m.", más largo y peor de comparar.
 */
export const hora = (iso) => (iso
  ? new Date(iso).toLocaleTimeString('es-MX', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  })
  : '');

/**
 * "45 s", "1 min 50 s", "26 min", "3 h 12 min".
 *
 * Los segundos se conservan por debajo de diez minutos porque ahí es donde se
 * comparan de verdad: entre una sincronización de 1:50 y otra de 1:05 hay una
 * diferencia que "1 min" en ambas escondía.
 */
export const duracion = (desde, hasta) => {
  if (!desde) return '—';
  const fin = hasta ? new Date(hasta) : new Date();
  const s = Math.max(Math.round((fin - new Date(desde)) / 1000), 0);
  if (s < 60) return `${s} s`;

  const min = Math.floor(s / 60);
  const resto = s % 60;
  if (min < 10) return resto > 0 ? `${min} min ${resto} s` : `${min} min`;
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${min % 60} min`;
};

export const ORIGEN = {
  manual: 'Manual',
  programada: 'Automática',
  masiva: 'Masiva',
};

/**
 * Traduce los mensajes crudos que dejaron las sincronizaciones del flujo viejo.
 *
 * En el histórico conviven registros con textos técnicos en dos idiomas
 * ("Verification failed: No se encontro la informacion"). El registro se
 * conserva tal cual en la base —es la auditoría—, pero en pantalla se cuenta en
 * el idioma de quien lo lee.
 */
export const enCastellano = (mensaje) => {
  const t = String(mensaje || '');
  if (/no se encontro la informacion/i.test(t)) {
    return 'El SAT no reconoció esa solicitud, así que no llegó a entregar nada.';
  }
  if (/getResponse is not a function/i.test(t)) {
    return 'Se cortó la conexión con el SAT mientras preparaba los paquetes.';
  }
  if (/Certificado Revocado|Certi.*revocad/i.test(t)) {
    return 'El SAT rechazó la e.firma de la empresa: parece revocada o caduca.';
  }
  if (/ENOENT|no such file/i.test(t)) {
    return 'No se encontró el archivo de la e.firma de la empresa.';
  }
  return t;
};

/**
 * Qué trajo la sincronización, con los números que guardó el servidor.
 *
 * "Terminada · 0 recibos" no distinguía entre que el SAT no trajo nada y que
 * trajo 117 que ya estaban en el sistema. Son cosas muy distintas, y la segunda
 * es lo normal al repetir un periodo.
 */
export const contarRecibos = (s) => {
  const entregados = s?.sat_cfdis_reported ?? 0;
  const nuevos = s?.receipts_new ?? 0;
  const yaEstaban = s?.receipts_existing ?? 0;
  const errores = s?.receipts_errors ?? 0;

  // Las solicitudes anteriores a jul-2026 no guardaron el desglose: de ellas
  // solo consta cuántos recibos entraron, así que se cuenta eso y nada más.
  if (entregados === 0) {
    if (nuevos > 0) return `Se agregaron ${nuevos} recibo(s) nuevo(s).`;
    return 'El SAT no entregó recibos para ese periodo.';
  }

  const partes = [`${nuevos} nuevo(s)`];
  if (yaEstaban > 0) partes.push(`${yaEstaban} que ya estaban`);
  if (errores > 0) partes.push(`${errores} que no se pudieron leer`);
  return `El SAT entregó ${entregados} recibo(s): ${partes.join(', ')}.`;
};
